import { ColumnWidth, GridCacheConfig, GridColumnDividerSettings, GridResizedEvent, GridSorting, GridSortingEvent, GridSummary, ScreenSize, ServiceGridColumn } from '@skylab-angular-shared/ui';
import { ConfigType, ViewSettingsRepositoryBase } from '@skylab-shared/business';
import { BehaviorSubject } from 'rxjs';

export type ServiceGridDefaultOptions<TSortingIdentity, TColumnIdentity, TEntity> = {
    columns: BehaviorSubject<ServiceGridColumn<TColumnIdentity, TSortingIdentity, TEntity, any, any>[]>;
    sorting: GridSorting<TSortingIdentity>;
    columnDividerTouched: GridColumnDividerSettings;
    summary?: GridSummary;
};

export abstract class ServiceGridServiceBase<
    TEntity,
    TColumnIdentity,
    TSortingIdentity,
    TConfig extends {[key: string]: GridCacheConfig<TColumnIdentity, TSortingIdentity>},
    > {
    protected abstract configKey: ConfigType;
    protected cacheData!: TConfig;

    protected constructor (
        private readonly viewSettingsRepository: ViewSettingsRepositoryBase
    ) { }

    protected setLoadedColumnsConfig(baseColumnsSubject: BehaviorSubject<ServiceGridColumn<TColumnIdentity, TSortingIdentity, TEntity, any, any>[]>, savedConfig: GridCacheConfig<TColumnIdentity, TSortingIdentity>): boolean {
        let oldConfig = false;
        const baseColumns = [...baseColumnsSubject.getValue()];

        savedConfig.columns.forEach(elem => {
            const baseColumn = baseColumns.find(col => col.identityName === elem.identityName);
            if (baseColumn) {
                baseColumn.width = elem.width === '*' ? baseColumn.minWidth : elem.width as ColumnWidth || baseColumn.minWidth;
                baseColumn.isHidden = elem.isHidden ?? baseColumn.isHidden;
            } else {
                oldConfig = true;
            }
        });

        const configAvailableVisibleColumns = savedConfig.columns.filter(elem => baseColumns.map(col => col.identityName).includes(elem.identityName));

        baseColumnsSubject.next(baseColumns.sort((item1, item2) => {
            const isInConfig = (item: ServiceGridColumn<TColumnIdentity, TSortingIdentity, TEntity, any, any>) => configAvailableVisibleColumns.map(elem => elem.identityName).includes(item.identityName);

            const isItem1InConfig = isInConfig(item1);
            const isItem2InConfig = isInConfig(item2);
            if (!isItem1InConfig || (isItem1InConfig && !isItem2InConfig)) {
                return 0;
            } else {
                const index1 = savedConfig.columns.map(elem => elem.identityName).indexOf(item1.identityName);
                const index2 = savedConfig.columns.map(elem => elem.identityName).indexOf(item2.identityName);
                return index1 - index2;
            }
        }));

        return oldConfig;
    }

    protected setLoadedSortingConfig(sorting: GridSorting<TSortingIdentity>, columns: ServiceGridColumn<TColumnIdentity, TSortingIdentity, TEntity, any, any>[], savedConfig: GridCacheConfig<TColumnIdentity, TSortingIdentity>): void {
        sorting.direction = savedConfig.orderingSettings?.direction ?? sorting.direction;
        sorting.key = columns.find(elem => elem.identityName === savedConfig.orderingSettings?.identityName)?.sortingBy || sorting.key;
    }

    protected setLoadedSummaryConfig(summary: GridSummary, savedConfig: GridCacheConfig<TColumnIdentity, TSortingIdentity>): void {
        if (savedConfig.summary) {
            summary.isVisible = savedConfig.summary.isVisible;
        }
    }

    protected async loadCachedConfigAsync(): Promise<TConfig> {
        return await this.viewSettingsRepository.getViewSettingsAsync<TConfig>(this.configKey);
    }

    protected addMissingColumnsToCache(columns: ServiceGridColumn<TColumnIdentity, TSortingIdentity, TEntity, any, any>[], savedConfig: GridCacheConfig<TColumnIdentity, TSortingIdentity>): void {
        const missingColumns = columns.filter(elem => !savedConfig.columns.map(cached => cached.identityName).includes(elem.identityName));
        missingColumns.forEach(missing => {
            const indexInCurrent = columns.findIndex(elem => elem.identityName === missing.identityName) ?? 0;
            savedConfig.columns.splice(indexInCurrent, 0, {
                identityName: missing.identityName,
                width: missing.width!,
                isHidden: missing.isHidden
            });
        });
    }

    protected getDefaultCacheConfig(defaults: ServiceGridDefaultOptions<TSortingIdentity, TColumnIdentity, TEntity>): GridCacheConfig<TColumnIdentity, TSortingIdentity> {
        return {
            columns: defaults.columns.value.map(elem => ({identityName: elem.identityName, width: elem.width || '*', isHidden: elem.isHidden ?? false})),
            orderingSettings: { direction: defaults.sorting.direction!, identityName: defaults.columns.value.find(elem => elem.sortingBy === defaults.sorting.key)?.identityName as TColumnIdentity},
            columnDividerTouched: {},
            summary: defaults.summary ? {...defaults.summary} : undefined
        };
    }

    private async saveConfigAsync(): Promise<void> {
        this.viewSettingsRepository.saveViewSettingsAsync(this.configKey, this.cacheData);
    }

    public saveDefaults(key: keyof TConfig, defaults: ServiceGridDefaultOptions<TSortingIdentity, TColumnIdentity, TEntity>): void {
        this.cacheData = {
            ...this.cacheData,
            [key]: this.getDefaultCacheConfig(defaults)
        };
        this.saveConfigAsync();
    }

    public onSummaryVisibleRowChange(key: keyof TConfig): void {
        this.cacheData[key].summary = {
            isVisible: !this.cacheData[key].summary?.isVisible
        };

        this.saveConfigAsync();
    }

    public onGridColumnResizeChange(data: {event: GridResizedEvent<TEntity>, identityName: TColumnIdentity}, key: keyof TConfig): void {
        const column = this.cacheData[key].columns.find(elem => elem.identityName === data.identityName);
        if (column) {
            column.width = data.event.columnWidth;
        }

        this.cacheData[key].columnDividerTouched = {
            ...this.cacheData[key].columnDividerTouched,
            [ScreenSize.getDeviceWidth()]: true
        };

        this.saveConfigAsync();
    }

    public onGridDisplayedColumnChange(columns: Map<TColumnIdentity, boolean>, key: keyof TConfig): void {
        this.cacheData[key].columns.forEach( elem => {
            elem.isHidden = !columns.get(elem.identityName);
        });

        const order = [...columns.entries()].map(elem => elem[0]);
        this.cacheData[key].columns = this.cacheData[key].columns.sort((item1, item2) => order.indexOf(item1.identityName) - order.indexOf(item2.identityName));

        this.saveConfigAsync();
    }

    public onGridSortingChange(event: GridSortingEvent<TColumnIdentity>, key: keyof TConfig): void {
        this.cacheData[key].orderingSettings = {
            direction: event.direction!,
            identityName: event.key
        };

        this.saveConfigAsync();
    }
}
